'use client';

import { useTelegramConfigsFindOneByKey } from '@/generated';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { IconChevronLeft } from '@tabler/icons-react';
import { Loading } from './Loading';
import React, { useCallback, useMemo } from 'react';
import Image from 'next/image';
import { TooltipedElem } from '../../../TooltipedElem';
import { useTranslations } from 'next-intl';
import { copyToClipboard } from '@/lib/notify';
import { useDelayedFlag } from '@/hooks/useDelayedFlag.hook';

type Props = {
  onClose: () => void;
  onBack: () => void;
  currencySign: string;
  totalSum: string;
};

export const BankPayment: React.FC<Props> = ({ onClose, onBack, currencySign, totalSum }) => {
  const t = useTranslations('monetization');
  const toastT = useTranslations('toasts');
  const { data: config, isLoading } = useTelegramConfigsFindOneByKey('MANUAL_PAYMENT_INFO');
  const parsedConfigData = useMemo(() => {
    if (isLoading) {
      return { title: '', text: '' };
    }
    return JSON.parse(config!.data);
  }, [config, isLoading]);

  const copy = useCallback(() => {
    copyToClipboard(parsedConfigData.text, { error: toastT('copy_error') });
  }, [parsedConfigData, toastT]);

  const showLoading = useDelayedFlag(isLoading);

  if (isLoading) {
    return showLoading ? <Loading onClose={onClose} /> : null;
  }
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-xl'>
        <Button
          variant='ghost'
          size='icon'
          onClick={onBack}
          className='absolute top-4 start-4 z-10 h-8 w-8 rounded-full hover:bg-transparent'
        >
          <IconChevronLeft className='text-text-primary' />
        </Button>
        <div className='flex flex-col gap-[24px]'>
          <DialogHeader className='items-center text-center'>
            <DialogTitle>{t('bank_transfer_title')}</DialogTitle>
          </DialogHeader>

          <div className='flex justify-between items-center'>
            <div className='text-text-disabled-day'>{t('bank_transfer_amount_label')}</div>

            <div className='font-semibold text-[20px] text-primary'>
              {totalSum} {currencySign}
            </div>
          </div>

          <div className='whitespace-pre-line pt-[20px] flex flex-col gap-[8px]'>
            <div className='flex gap-[8px] items-start'>
              <h3 className='text-primary text-[16px] font-semibold flex-grow-2'>{parsedConfigData.title}</h3>
              <TooltipedElem onClick={copy} tooltipText={t('bank_transfer_copied')}>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-auto w-[20px] min-w-[20px] p-0 mt-[4px] hover:bg-transparent'
                >
                  <Image src='/assets/icons/copy-icon.svg' width={20} height={20} alt='' />
                </Button>
              </TooltipedElem>
            </div>

            {parsedConfigData.text}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
